import type { AsciiName } from "@/content/ascii"
import type { OverlayKind, Phase, Round, SessionConfig } from "@/types"

/** Round transition copy and destinations. */
interface OverlayCopy {
  /**
   * The chip above the title: which round of the workshop this is, and what it
   * is called out loud. Two facts, not one — the number tells the room where it
   * is in the hour, the word tells it what the next twenty minutes are about.
   * Both are decoration for a reader; the title below says the same thing.
   */
  num: string
  badge: string
  title: string
  body: string
  /** The motif above the title. Decoration — `AsciiArt` hides it from readers. */
  motif: AsciiName
  /**
   * The three beats of the round, numbered on the curtain.
   *
   * They are the whole reason a curtain is worth a full screen: the facilitator
   * reads them out, and a room that has been told what the three steps are does
   * not have to be told again inside each one. A curtain with none is one that
   * has nothing to explain — the job gate, which asks a question instead.
   */
  steps?: readonly { label: string; detail: string }[]
  /** The facilitator's button. */
  cta: string
  /** Where the CTA takes the room. Every transition moves it somewhere. */
  destination: { round: Round; phase: Phase }
  /**
   * The second way out, on the one overlay that has a choice. Only `jobGate`
   * sets it: every other curtain is "the room is about to do this", and offering
   * an alternative would imply the round was optional when it is not.
   */
  altCta?: string
  altDestination?: { round: Round; phase: Phase }
}

/**
 * The curtain that goes up before a round, keyed by the round it opens.
 *
 * Rounds are entered through a curtain rather than appearing, because the room is
 * physically together and needs a beat to look up — the screen changing under a
 * conversation is how a facilitator loses it.
 *
 * `null` covers the three rounds nothing announces: `lobby`, which nothing
 * advances into, `wrap`, which is reached from the job gate, and `job`, whose
 * interstitial ASKS rather than announces — which pain becomes the job — and is
 * therefore raised by `RoundControls` alongside the gate, since only it knows
 * whether there is a vote to choose from. See `jobPick`.
 */
export function curtainFor(round: Round): OverlayKind | null {
  switch (round) {
    case "pains":
      return "ready"
    case "cards":
      return "deck"
    case "priorities":
      return "board"
    case "deep":
      return "dive"
    case "lobby":
    case "job":
    case "wrap":
      return null
  }
}

export const OVERLAY_WAIT = "Waiting for the facilitator"
export const GATE_WAIT = "The facilitator is deciding what comes next"
export const PICK_WAIT = "The facilitator is choosing the problem"

/**
 * The beat between closing the last job and the leave-behind appearing.
 *
 * The sheet is derived, so there is nothing to wait for — but the room has just
 * spent an hour producing it, and having it appear instantly reads as navigation
 * rather than as an outcome. The tally names what is ON the sheet, so the pause
 * reports the session back rather than just filling time.
 *
 * Real counts, not decoration: a room that has just watched three numbers be
 * written out knows how much it did, and a fake one would be found out the
 * moment somebody reads the report underneath.
 */
export interface ReportTally {
  /** Problems the room took on. One section of the report each. */
  jobs: number
  /** Pain points that won at least one vote. */
  painsVoted: number
  /** Cards filed on a priorities board, across every job. */
  cardsPlaced: number
}

/** How wide a tally line is set. Fixed, so the dot leaders line up. */
const TALLY_WIDTH = 30

/*
 * `label ....... 12`. Built rather than written out because the leader has to
 * absorb the width of a number nobody knows in advance — and because the line
 * is DECODED character by character on screen, so a line that changed length as
 * its number resolved would drag the whole column.
 */
function tallyLine(label: string, value: number): string {
  const count = String(value)
  const dots = Math.max(1, TALLY_WIDTH - label.length - count.length - 2)
  return `${label} ${".".repeat(dots)} ${count}`
}

export const GENERATING = {
  title: "Loading report…",
  /** The last line is the outcome, not a count — the screen's whole point. */
  ready: "── report ready",
  lines: (tally: ReportTally) => [
    tallyLine("jobs-to-be-done", tally.jobs),
    tallyLine("pains voted", tally.painsVoted),
    tallyLine("cards placed", tally.cardsPlaced),
    GENERATING.ready,
  ],
  /*
   * The same tally, said rather than drawn.
   *
   * The lines above are set to a fixed width with dot leaders, which a screen
   * reader reads as a run of full stops, and the `──` on the last one as
   * nothing at all. Neither is copy — they are how the column is measured — so
   * the announcement is written out separately. See `GeneratingScreen`, which
   * hides the drawn tally from the live region and reads this instead. It does
   * not repeat the title, which is in the same region and already read.
   */
  spoken: (tally: ReportTally) =>
    `${tally.jobs} jobs-to-be-done, ${tally.painsVoted} pain points voted, ${tally.cardsPlaced} cards placed. Report ready.`,
} as const

/**
 * Keyed by kind.
 *
 * The text is the `Report generation & Break screens explorations.dc` doc's,
 * verbatim — that file is the source of truth for what a curtain says, and the
 * copy here drifted from it once already. `config` is still read because the
 * job gate genuinely offers a different second answer with the Pains round off.
 */
export function overlayCopy(kind: OverlayKind, config: SessionConfig | null): OverlayCopy {
  switch (kind) {
    case "ready":
      return {
        num: "01",
        badge: "problem",
        title: "Name the pain points",
        body: "Silent writing first. Everyone gets some time to write what hurts. The top pain point is the one this session runs on.",
        motif: "pains",
        steps: [
          {
            label: "Write",
            detail: "In silence. Be specific: a real moment, who it hit, what it cost.",
          },
          { label: "Reveal", detail: "Read out loud, no commentary." },
          { label: "Vote", detail: "Two votes each. The top pain goes forward." },
        ],
        cta: "Start round one",
        destination: { round: "pains", phase: "write" },
      }
    case "deck":
      /*
       * No `painsEnabled` branch any more. It used to read "One problem locked"
       * against "No warm-up. The room goes straight to the cards", because the
       * first sentence was a lie when there had been no vote. The approved copy
       * is about reading the deck against the job, and there is always a job, so
       * one text is true either way.
       */
      return {
        num: "03",
        badge: "deck of cards",
        title: "Explore the deck",
        body: "Every capability, read against the job. Each person nominates the five that could move it.",
        motif: "deck",
        steps: [
          { label: "Read", detail: "Every capability, read against the job." },
          { label: "Nominate", detail: "Five each that could move the job." },
          { label: "Shortlist", detail: "The room's top six go to the fit board." },
        ],
        cta: "Open the deck",
        destination: { round: "cards", phase: "pick" },
      }
    case "board":
      return {
        num: "04",
        badge: "fit board",
        title: "Set the priorities",
        body: "The hand is dealt. Now agree where every card belongs.",
        motif: "fit",
        steps: [
          { label: "One at a time", detail: "The facilitator reads a card. The room argues." },
          { label: "Place it", detail: "Must, Should, Could, or not this pass." },
          { label: "Say why", detail: "One line on each keeper, in the room's own words." },
        ],
        cta: "Open the board",
        destination: { round: "priorities", phase: "place" },
      }
    case "dive":
      return {
        num: "05",
        badge: "deep dive",
        title: "Go deep on the keepers",
        body: "The board is set. Now take the Must haves apart, card by card.",
        motif: "hand",
        steps: [
          { label: "Pick a card", detail: "The facilitator chooses; every screen follows." },
          {
            label: "Four boxes",
            detail: "What it does, who it is for, what it needs, what to watch.",
          },
          { label: "Move on", detail: "Good enough beats complete. The next card is waiting." },
        ],
        cta: "Start the deep dive",
        destination: { round: "deep", phase: "fill" },
      }
    case "wrap":
      // Nothing raises this any more — the report is reached from the job gate.
      // Copy is kept so a session written by an older build still renders a
      // curtain someone can dismiss, rather than a blank screen.
      return {
        num: "06",
        badge: "wrap",
        title: "That’s a wrap",
        body: "Everything the room decided is written up. Let’s read it back.",
        motif: "hand",
        cta: "Open the read",
        destination: { round: "wrap", phase: "summary" },
      }
    case "jobPick":
      /*
       * The vote is counted and the room decides what to do about it. The top
       * pain is NOT converted on the facilitator's behalf: a tie has no top pain
       * at all, and even a clear winner is regularly set aside in the room ("we
       * can't touch that one this quarter") — a job opened before anyone said so
       * had to be worked or abandoned, and abandoning it leaves an empty job in
       * the report. So the same list the gate offers is offered here, ordered by
       * votes, with the CTA naming the one action this screen has.
       */
      return {
        num: "02",
        badge: "job to be done",
        title: "Which problem are we taking on?",
        body: "The votes are in. Pick the pain point this job runs on — the room's count is a recommendation, not the decision.",
        motif: "job",
        cta: "Explore this problem",
        destination: { round: "job", phase: "draft" },
      }
    case "jobGate":
      /*
       * The other overlay that asks rather than announces (see `jobPick`), and
       * the one place a session becomes more than one problem — same list, same
       * write, a second way out. Finishing is the primary action
       * because most sessions work one job; taking another needs a pain picked
       * first, which is what `overlay.selection` holds.
       */
      return {
        num: "06",
        badge: "wrap",
        title: "Tackle another problem?",
        body:
          config?.painsEnabled === false
            ? "The first problem is done. Write another one, or wrap the session up with the report."
            : "The first problem is done. Run another voted pain point through the same rounds, or wrap the session up with the report.",
        motif: "hand",
        cta: "Finish and create report",
        destination: { round: "wrap", phase: "summary" },
        altCta: "Explore this problem",
        altDestination: { round: "job", phase: "draft" },
      }
  }
}
